import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import {
  FaArrowRight,
  FaChevronDown,
  FaEnvelope,
  FaFileAlt,
  FaHeadset,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaShieldAlt,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import NavBar from "./components/nav/Nav";
import "./Help.css";

const frequentlyAskedQuestions = [
  { question: "faq_q_create_account", answer: "faq_a_create_account" },
  {
    question: "faq_q_complete_preenrolment",
    answer: "faq_a_complete_preenrolment",
  },
  {
    question: "faq_q_track_application",
    answer: "faq_a_track_application",
  },
  {
    question: "faq_q_forgot_password",
    answer: "faq_a_forgot_password",
  },
  { question: "faq_q_documents", answer: "faq_a_documents" },
  { question: "faq_q_update_info", answer: "faq_a_update_info" },
];

const helpTopics = [
  {
    title: "topic_create_account",
    description: "topic_create_account_desc",
    icon: FaUserCheck,
    color: "mint",
  },
  {
    title: "topic_preenrolment_process",
    description: "topic_preenrolment_process_desc",
    icon: FaFileAlt,
    color: "mint",
  },
  {
    title: "topic_account_security",
    description: "topic_account_security_desc",
    icon: FaShieldAlt,
    color: "mint",
  },
  {
    title: "topic_track_application",
    description: "topic_track_application_desc",
    icon: FaUsers,
    color: "mint",
  },
];

const Help = () => {
  const { t } = useTranslation();
  const [openQuestion, setOpenQuestion] = useState(null);
  const [messageSent, setMessageSent] = useState(false);

  const sendMessage = (event) => {
    event.preventDefault();
    setMessageSent(true);
  };

  return (
    <div className="help-experience">
      <NavBar activePage="help" />

      <section className="help-hero" aria-label={t("help_support")} />

      <main className="help-content">
        <section className="help-topics" aria-labelledby="topics-heading">
          <div className="section-eyebrow">
            <span /> {t("popular_help_topics")}
          </div>
          <h1 id="topics-heading" className="visually-hidden">
            {t("popular_help_topics")}
          </h1>
          <div className="topic-grid">
            {helpTopics.map(({ title, description, icon: Icon, color }) => (
              <button
                key={title}
                type="button"
                className={`topic-card ${color}`}
                onClick={() => setOpenQuestion(0)}
              >
                <span className="topic-icon">
                  <Icon />
                </span>
                <span className="topic-copy">
                  <strong>{t(title)}</strong>
                  <small>{t(description)}</small>
                </span>
                <FaArrowRight className="topic-arrow" aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>

        <section className="help-details">
          <article className="faq-panel" aria-labelledby="faq-heading">
            <div className="faq-heading-row">
              <div>
                <div className="section-eyebrow">
                  <span /> {t("faq")}
                </div>
                <h2 id="faq-heading">{t("faq_frequently_asked")}</h2>
              </div>
              <button
                type="button"
                className="view-all"
                onClick={() => setOpenQuestion(null)}
              >
                {t("view_all_faqs")} <FaArrowRight />
              </button>
            </div>
            <div className="faq-list">
              {frequentlyAskedQuestions.map(({ question, answer }, index) => {
                const isOpen = openQuestion === index;
                return (
                  <div className="faq-item" key={question}>
                    <button
                      type="button"
                      onClick={() => setOpenQuestion(isOpen ? null : index)}
                      aria-expanded={isOpen}
                    >
                      <span>{t(question)}</span>
                      <FaChevronDown aria-hidden="true" />
                    </button>
                    {isOpen && <p>{t(answer)}</p>}
                  </div>
                );
              })}
            </div>
          </article>

          <aside className="support-panel" aria-labelledby="support-heading">
            <div className="section-eyebrow">
              <span /> {t("contact_support")}
            </div>
            <FaHeadset className="support-art" aria-hidden="true" />
            <h2 id="support-heading">{t("here_to_help")}</h2>
            <p>
              {t("support_ready")}
            </p>
            <a className="support-detail" href="mailto:support@bioenroll.mg">
              <FaEnvelope />
              <span>
                <small>{t("email_us")}</small>
                <strong>support@bioenroll.mg</strong>
              </span>
            </a>
            <div className="support-detail">
              <FaMapMarkerAlt />
              <span>
                <small>{t("service_centre")}</small>
                <strong>Antananarivo, Madagascar</strong>
              </span>
            </div>
            <a className="support-action" href="mailto:support@bioenroll.mg">
              {t("contact_support_action")} <FaArrowRight />
            </a>
          </aside>
        </section>

        <section className="message-panel" aria-labelledby="message-heading">
          <div className="message-intro">
            <FaPaperPlane aria-hidden="true" />
            <div>
              <div className="section-eyebrow">
                <span /> {t("send_us_message")}
              </div>
              <h2 id="message-heading">
                {t("have_question")}
                <br />
                <em>{t("send_us_message")}.</em>
              </h2>
              <p>
                {t("fill_form_support")}
              </p>
            </div>
          </div>
          <form className="message-form" onSubmit={sendMessage}>
            <label>
              {t("full_name")}
              <input name="name" required placeholder={t("enter_full_name")} />
            </label>
            <label>
              {t("email_address")}
              <input
                name="email"
                type="email"
                required
                placeholder={t("enter_email_address")}
              />
            </label>
            <label>
              {t("subject")}
              <select name="subject" defaultValue="">
                <option value="" disabled>
                  {t("select_subject")}
                </option>
                <option>{t("account_access")}</option>
                <option>{t("preenrolment_process")}</option>
                <option>{t("application_tracking")}</option>
              </select>
            </label>
            <label className="message-field">
              {t("message")}
              <textarea
                name="message"
                required
                placeholder={t("tell_us_help")}
              />
            </label>
            <button type="submit">
              {messageSent ? t("message_sent") : t("send_message")} <FaArrowRight />
            </button>
          </form>
        </section>
      </main>
    </div>
  );
};

export default Help;
